import { isValid } from 'date-fns';
import { logger } from './logger';

/**
 * Branded type for date-only values (yyyy-mm-dd format), for specific cases
 * where the system needs a strict date-only value, regardless of timezone.
 *
 * This is for use in specific business cases (OTC's and Care Orders are two examples) where the user enters a date, and we want to show that
 * specific date on the UI (across any timezone).  No TimeZone conversion is expected or allowed.
 *
 * As of time of writing, the backend is still expecting the storage to be in full timestamp UTC, but in the future there
 * are plans to store yyyy-mm-dd format for these cases.
 */
export type DateOnly = string & { readonly __brand: 'DateOnly' };

/**
 * Options for DateOnly constructor
 */
export interface DateOnlyOptions {
  /** Time to use for ISO output (defaults to '00:00:00Z' for UTC midnight)
   * - Input time is ignored, will just strip out the time component
   */
  outputTime?: string;
}

/**
 * Default output time for DateOnly conversions (UTC midnight)
 */
const DEFAULT_OUTPUT_TIME = '00:00:00.000Z';

/**
 * Type guard to check if a value is a valid DateOnly string
 * @param value - The value to check
 * @returns true if the value is a DateOnly string (yyyy-mm-dd format)
 */
export function isDateOnly(value: unknown): value is DateOnly {
  if (typeof value !== 'string') {
    return false;
  }

  // Check basic format
  const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateOnlyRegex.test(value)) {
    return false;
  }

  // Validate that it's a real date
  const testDate = new Date(value + 'T00:00:00Z');
  if (!isValid(testDate)) {
    return false;
  }
  if (testDate.toISOString().slice(0, 10) !== value) {
    return false;
  }

  return true;
}

/**
 * DateOnly class for date operations and utilities for the {@link DateOnly} type.
 * - Provides methods for validating, formatting, and manipulating date-only strings (yyyy-mm-dd format).
 */
export class DateOnlyClass {
  private readonly value: DateOnly | null;
  private readonly outputTime: string;

  constructor(input: string, options: DateOnlyOptions = {}) {
    const { outputTime = DEFAULT_OUTPUT_TIME } = options;

    // Validate outputTime format
    if (!isValidIsoTime(outputTime)) {
      logger.warn(
        `Invalid ISO time format in DateOnly options: ${outputTime}. Expected format like 'HH:MM:SS.sssZ' or 'HH:MM:SSZ'.`,
        {
          stack: new Error().stack,
        }
      );
      this.value = null;
      this.outputTime = DEFAULT_OUTPUT_TIME; // fallback to default
      return;
    }

    // Validate input format
    const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateOnlyRegex.test(input)) {
      logger.warn(`Invalid DateOnly format: ${input}. Expected yyyy-mm-dd format.`, {
        stack: new Error().stack,
      });
      this.value = null;
      this.outputTime = outputTime;
      return;
    }

    // Validate that it's a real date by checking if the formatted date matches the input
    const testDate = new Date(input + 'T00:00:00Z');
    if (!isValid(testDate)) {
      logger.warn(`Invalid DateOnly date: ${input}. Not a valid calendar date.`, {
        stack: new Error().stack,
      });
      this.value = null;
      this.outputTime = outputTime;
      return;
    }

    const utcFormatted = testDate.toISOString().slice(0, 10);
    if (utcFormatted !== input) {
      logger.warn(`Invalid DateOnly date: ${input}. UTC formatted date ${utcFormatted} does not match input.`, {
        stack: new Error().stack,
      });
      this.value = null;
      this.outputTime = outputTime;
      return;
    }

    this.value = input as DateOnly;
    this.outputTime = outputTime;
  }

  /**
   * Get the date string value
   */
  get dateValue(): DateOnly | null {
    return this.value || null;
  }

  /**
   * Get the date as a Date object (UTC midnight)
   */
  get date(): Date | null {
    if (!this.value) {
      return null;
    }
    return new Date(this.value + 'T00:00:00Z');
  }

  /**
   * Convert to a Date object using the instance's outputTime
   * @returns Date object or null if dateValue is null
   * @throws Error if the instance's outputTime is not a valid ISO time format
   */
  toDate(): Date | null {
    const result = dateOnlyToDate(this.value, this.outputTime);
    return result || null;
  }

  /**
   * Get the date as an ISO string with configured output time
   */
  get isoString(): string | null {
    if (!this.value) {
      return null;
    }
    return `${this.value}T${this.outputTime}`;
  }

  /**
   * Add days to the date
   */
  addDays(days: number): DateOnly | null {
    if (!this.value) {
      return null;
    }

    const currentDate = this.date;
    if (!currentDate) {
      return null;
    }

    const newDate = new Date(currentDate);
    newDate.setUTCDate(newDate.getUTCDate() + days);
    const newDateString = newDate.toISOString().slice(0, 10);

    return new DateOnlyClass(newDateString, { outputTime: this.outputTime }).dateValue;
  }

  /**
   * Subtract days from the date
   */
  subDays(days: number): DateOnly | null {
    return this.addDays(-days);
  }

  /**
   * Check if the date falls on a weekend (Saturday or Sunday)
   */
  get isWeekend(): boolean {
    if (!this.value) {
      return false;
    }

    const date = this.date;
    if (!date) {
      return false;
    }

    const dayOfWeek = date.getUTCDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
  }

  /**
   * Convert to string representation
   */
  toString(): string {
    return this.value || '';
  }

  /**
   * Check equality with another DateOnly
   */
  equals(other: DateOnly | null): boolean {
    if (!this.value || !other) {
      return this.value === other;
    }
    return this.value === other;
  }
}

/**
 * Create a DateOnly from a date string (yyyy-mm-dd or ISO format)
 * Strips time component from ISO strings
 */
export function createDateOnly(input: string, options?: DateOnlyOptions): DateOnly | null {
  // Handle empty or whitespace-only strings
  if (!input || !input.trim()) {
    return null;
  }

  // If it's an ISO string with timezone, extract the date from the original timezone
  if (input.includes('T') && (input.includes('Z') || input.includes('+') || input.includes('-'))) {
    // Parse the ISO string to extract the date part from the original timezone
    const datePart = input.split('T')[0];

    // Try DateOnlyClass first
    const dateOnlyResult = new DateOnlyClass(datePart, options).dateValue;
    if (dateOnlyResult) {
      return dateOnlyResult;
    }

    // Fallback: validate the date part ourselves for robustness with ISO dates
    // This handles cases where DateOnlyClass validation is too strict
    if (isValidDateOnlyFormat(datePart)) {
      return datePart as DateOnly;
    }

    return null;
  }

  // If it's an ISO string with space separator, extract just the date part
  if (input.includes(' ')) {
    const datePart = input.split(' ')[0];
    const dateOnlyResult = new DateOnlyClass(datePart, options).dateValue;
    if (dateOnlyResult) {
      return dateOnlyResult;
    }

    // Fallback for space-separated format
    if (isValidDateOnlyFormat(datePart)) {
      return datePart as DateOnly;
    }

    return null;
  }

  return new DateOnlyClass(input, options).dateValue;
}

/**
 * Validate that a string is a valid yyyy-mm-dd date format
 * More reliable than DateOnlyClass validation for ISO date extraction
 */
function isValidDateOnlyFormat(input: string): boolean {
  // Check format: yyyy-mm-dd
  const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateOnlyRegex.test(input)) {
    return false;
  }

  // Check if it's a valid date by parsing it
  const testDate = new Date(input + 'T00:00:00.000Z');
  if (isNaN(testDate.getTime())) {
    return false;
  }

  return testDate.toISOString().slice(0, 10) === input;
}

/**
 * Create a DateOnly from a Date object
 */
export function createDateOnlyFromDate(date: Date): DateOnly;
export function createDateOnlyFromDate(date: Date, options: DateOnlyOptions): DateOnly | null;
export function createDateOnlyFromDate(date: Date, options?: DateOnlyOptions): DateOnly | null {
  // For Date objects, we need to extract the calendar date from the local timezone
  // This is the most common case when working with Date objects
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateString = `${year}-${month}-${day}`;

  if (options) {
    return new DateOnlyClass(dateString, options).dateValue;
  } else {
    // When no options provided, we guarantee a DateOnly return
    return new DateOnlyClass(dateString).dateValue;
  }
}

/**
 * Create a DateOnly for today's date
 */
export function createDateOnlyToday(options?: DateOnlyOptions): DateOnly {
  if (options) {
    return createDateOnlyFromDate(new Date(), options)!;
  } else {
    return createDateOnlyFromDate(new Date());
  }
}

/**
 * Validates if a time string is a valid ISO time format
 * @param timeString - The time string to validate
 * @returns true if valid, false otherwise
 */
function isValidIsoTime(timeString: string): boolean {
  // Valid ISO time patterns:
  // HH:MM:SS.sssZ, HH:MM:SSZ, HH:MM:SS.sss+HH:MM, HH:MM:SS+HH:MM, etc.
  const isoTimeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9](\.\d{1,3})?(Z|[+-]([01]?[0-9]|2[0-3]):[0-5][0-9])$/;

  if (!isoTimeRegex.test(timeString)) {
    return false;
  }

  // Test if it creates a valid date when combined with a known date
  try {
    const testDate = new Date(`2024-01-01T${timeString}`);
    return !isNaN(testDate.getTime());
  } catch {
    return false;
  }
}

/**
 * Convert a DateOnly to a Date object with specified time
 * @param dateOnly - The DateOnly string to convert
 * @param outputTime - The time portion to append (defaults to '00:00:00.000Z' for UTC midnight)
 * @returns Date object or undefined if dateOnly is null/undefined
 * @throws Error if outputTime is not a valid ISO time format
 */
export function dateOnlyToDate(
  dateOnly: DateOnly | null | undefined,
  outputTime: string = DEFAULT_OUTPUT_TIME
): Date | undefined {
  if (!dateOnly) {
    return undefined;
  }

  if (!isValidIsoTime(outputTime)) {
    throw new Error(`Invalid ISO time format: ${outputTime}. Expected format like 'HH:MM:SS.sssZ' or 'HH:MM:SSZ'`);
  }

  const isoString = `${dateOnly}T${outputTime}`;
  const date = new Date(isoString);

  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date created from DateOnly: ${dateOnly} and outputTime: ${outputTime}`);
  }

  return date;
}
